import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Col, Modal, Row } from 'react-bootstrap'
import { Button, Card, Paragraph } from 'src/components'
import { faCheckDouble, faPlus } from '@fortawesome/free-solid-svg-icons'

import { actions, getThresholds, updateThresholds } from 'src/actions/thresholds.action'
import { selectThresholds } from 'src/selectors/thresholds.selector'

import { loadingSelector } from 'src/selectors/loading.selector'
import { haveAnyValue } from 'src/utils/utilitiesV2'

import { ThresholdDetail } from 'src/content/Threshold/ThresholdDetail'
import { ThresholdList } from 'src/content/Threshold/ThresholdList'
import { ThresholdEnum, ThresholdValues } from 'src/content/Threshold/ThresholdEnum'

import { handlerError, handlerSuccess, hasErrors } from 'src/selectors/error.selector'
import { showAlert } from 'src/actions/alert.actions'

interface ClientThresholdProps {
  show: boolean
  type: ThresholdEnum
  onHide: () => void
  posId?: number
}

interface ThresholdCRUD {
  show: boolean
  threshold?: IThreshold
}

interface IFlags {
  get: boolean
  update: boolean
}

export const Threshold = ({ type, show, onHide, posId }: ClientThresholdProps) => {
  const dispatch = useDispatch()

  const thresholdValue = ThresholdValues[type]

  const response: IThreshold[] = useSelector(selectThresholds)
  const loadingGet = useSelector(state =>
    loadingSelector([actions.GET_THRESHOLDS])(state),
  )

  const loadingUpdate = useSelector(state =>
    loadingSelector([actions.UPDATE_THRESHOLDS])(state),
  )
  const errorUpdate = useSelector(state => hasErrors([actions.UPDATE_THRESHOLDS])(state))

  const [flags, setFlags] = useState<IFlags>({
    get: false,
    update: false,
  })

  const [thresholds, setThresholds] = useState<IThreshold[]>([])

  const [thresholdDetail, setDetail] = useState<ThresholdCRUD>({
    show: false,
    threshold: undefined,
  })

  useEffect(() => {
    if (!show) return
    dispatch(getThresholds(type, posId))
  }, [show])

  useEffect(() => {
    if (loadingGet) setFlags({ ...flags, get: true })
    else if (flags.get) {
      setFlags({ ...flags, get: false })
      setThresholds(response)
    }
  }, [loadingGet])

  useEffect(() => {
    if (loadingUpdate) setFlags({ ...flags, update: true })
    else if (flags.update) {
      setFlags({ ...flags, update: false })
      const alert = errorUpdate ? handlerError(errorUpdate.message) : handlerSuccess()
      dispatch(showAlert(alert))
    }
  }, [loadingUpdate])

  const onClose = () => {
    onHide()
  }

  const onUpdateThreshold = (threshold: IThreshold, toDelete?: boolean) => {
    setDetail({ show: false })
    setThresholds(prev => {
      if (toDelete) return prev.filter(p => p.id !== threshold.id)
      else if (haveAnyValue(threshold.id)) {
        const index = prev.findIndex(p => p.id === threshold.id)
        prev[index] = threshold
        return prev
      } else return [...prev, { ...threshold, id: new Date().valueOf() }]
    })
  }

  const onSave = () => {
    const request: IThresholdRequest[] = thresholds.map(t => ({
      rgb: t.rgb,
      min: t.min,
      max: t.max,
    }))
    dispatch(updateThresholds(type, request, posId))
  }

  return (
    <div>
      <Modal show={show && !thresholdDetail.show} onHide={onClose} centered size={'lg'}>
        <Modal.Header closeButton>
          <Modal.Title>Rango de {thresholdValue.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col xl={12}>
              <Paragraph>{thresholdValue.description}</Paragraph>
            </Col>
            <Col xl={12}>
              <Row className={'container-buttons'}>
                <Button
                  icon={faPlus}
                  onClick={() => setDetail({ show: true })}
                  disabled={loadingUpdate}>
                  Nuevo
                </Button>
              </Row>
            </Col>
            <Col xl={12}>
              <Card white title={'Configuraciones'}>
                <ThresholdList
                  loading={loadingGet}
                  thresholds={thresholds}
                  onUpdate={threshold => setDetail({ show: true, threshold })}
                  onDelete={threshold => onUpdateThreshold(threshold, true)}
                />
              </Card>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Row className={'container-buttons'}>
            <Button icon={faCheckDouble} onClick={onSave} loading={loadingUpdate}>
              Actualizar
            </Button>
          </Row>
        </Modal.Footer>
      </Modal>

      <ThresholdDetail
        show={thresholdDetail.show}
        threshold={thresholdDetail.threshold}
        onHide={() => setDetail({ show: false })}
        onChange={onUpdateThreshold}
      />
    </div>
  )
}
